/**
 * Personalization localStorage utilities
 * 
 * Stores personalization data in localStorage with a TTL (time-to-live) of 10 minutes.
 * This is used as a fallback when backend cache returns stale data.
 * 
 * After 10 minutes, the localStorage data is considered stale and should be refreshed from server.
 */

import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";
import { DEFAULT_DASHBOARD_APPEARANCE } from "@/lib/theme/dashboard-appearance";
import type { ThemeModeControl, FontSize } from "@/components/dashboard/DashboardThemeProvider";

const STORAGE_KEY_PREFIX = "bcm:personalization:";
const TTL_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

interface StoredPersonalization {
  tenantId: number;
  colorPalette: ColorPaletteId;
  customColor: string | null;
  grainEnabled: boolean;
  gradientEnabled: boolean;
  themeModeControl: ThemeModeControl;
  fontSize: FontSize;
  dashboardAppearance?: DashboardAppearance;
  timestamp: number; // Unix timestamp in milliseconds
}

export type StoredPersonalizationData = {
  colorPalette: ColorPaletteId;
  customColor: string | null;
  grainEnabled: boolean;
  gradientEnabled: boolean;
  themeModeControl: ThemeModeControl;
  fontSize: FontSize;
  dashboardAppearance: DashboardAppearance;
};

/**
 * Get storage key for a specific tenant
 */
function getStorageKey(tenantId: number): string {
  return `${STORAGE_KEY_PREFIX}${tenantId}`;
}

/**
 * Save personalization data to localStorage with timestamp
 */
export function savePersonalizationToStorage(
  tenantId: number,
  data: StoredPersonalizationData,
): void {
  if (typeof window === "undefined") {
    return; // SSR safety
  }

  try {
    const stored: StoredPersonalization = {
      tenantId,
      ...data,
      timestamp: Date.now(),
    };

    const key = getStorageKey(tenantId);
    localStorage.setItem(key, JSON.stringify(stored));
  } catch (error) {
    console.error("Error saving personalization to localStorage:", error);
    // Don't throw - localStorage might be disabled or full
  }
}

/**
 * Get personalization data from localStorage if it's still fresh (within TTL)
 * Returns null if data is stale, missing, or invalid
 */
export function getPersonalizationFromStorage(
  tenantId: number,
): StoredPersonalizationData | null {
  if (typeof window === "undefined") {
    return null; // SSR safety
  }

  try {
    const key = getStorageKey(tenantId);
    const storedJson = localStorage.getItem(key);
    
    if (!storedJson) {
      return null;
    }

    const stored: StoredPersonalization = JSON.parse(storedJson);

    // Validate structure
    if (
      !stored ||
      typeof stored.tenantId !== "number" ||
      !stored.colorPalette ||
      typeof stored.grainEnabled !== "boolean" ||
      typeof stored.timestamp !== "number"
    ) {
      localStorage.removeItem(key);
      return null;
    }

    // Check if data is still fresh (within TTL)
    const age = Date.now() - stored.timestamp;
    if (age > TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return {
      colorPalette: stored.colorPalette,
      customColor: stored.customColor ?? null,
      grainEnabled: stored.grainEnabled,
      gradientEnabled: stored.gradientEnabled ?? false,
      themeModeControl: stored.themeModeControl ?? "user-choice",
      fontSize: stored.fontSize ?? "medium",
      dashboardAppearance: stored.dashboardAppearance ?? DEFAULT_DASHBOARD_APPEARANCE,
    };
  } catch (error) {
    console.error("Error reading personalization from localStorage:", error);
    // Remove corrupted data
    try {
      const key = getStorageKey(tenantId);
      localStorage.removeItem(key);
    } catch {
      // Ignore errors when removing
    }
    return null;
  }
}

/**
 * Clear personalization data from localStorage for a specific tenant
 */
export function clearPersonalizationFromStorage(tenantId: number): void {
  if (typeof window === "undefined") {
    return; // SSR safety
  }

  try {
    const key = getStorageKey(tenantId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing personalization from localStorage:", error);
  }
}

/**
 * Check if personalization data exists in localStorage and is fresh
 */
export function hasFreshPersonalizationInStorage(tenantId: number): boolean {
  return getPersonalizationFromStorage(tenantId) !== null;
}

/**
 * Get the age of stored personalization data in milliseconds
 * Returns null if data doesn't exist or is invalid
 */
export function getPersonalizationStorageAge(tenantId: number): number | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const key = getStorageKey(tenantId);
    const storedJson = localStorage.getItem(key);
    
    if (!storedJson) {
      return null;
    }

    const stored: StoredPersonalization = JSON.parse(storedJson);
    
    if (!stored || typeof stored.timestamp !== "number") {
      return null;
    }

    return Date.now() - stored.timestamp;
  } catch {
    return null;
  }
}

